'use client';

import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PortalLink, tx, useCurrentUser, useLocale } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { sectionInner, sectionPad, cardReset } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { HeadingLine } from '../_shared/HeadingLine';
import { ActivityApplicationModal } from './ActivityApplicationModal';
import { fullVenue, shortVenue } from './venue';
import { BlockRoot } from '../_shared/BlockRoot';

/**
 * Data-driven grid of activity/event cards.
 *
 * <p>Unlike the {@code CourseRow} rail (which goes
 * through the {@code LearningSource} adapter), this block fetches the
 * portal route {@code /api/activities} DIRECTLY — the route proxies to
 * the backend's tenant-scoped event search
 * ({@code POST /v1/public/{tenantId}/employee/external/events/search})
 * with auth/tenant/language resolved server-side. The operator edits a
 * raw query config (firstResult / maxResults / sortField / sortOrder /
 * filter) in the admin; that config is POSTed verbatim.
 *
 * <p>The backend groups results into three buckets (upcoming / missed /
 * previous); {@link ActivityRowProps.bucket} picks which one(s) to
 * render. Each event maps to one {@code <article>} card styled with the
 * theme's CSS-variable tokens (no hard-coded utility classes).
 */
export interface ActivityFilter {
  eventTypeIds?: Array<number | string>;
  cityIds?: Array<number | string>;
  categoryIds?: Array<number | string>;
  educationLevels?: string[];
  statuses?: string[] | null;
  searchTerm?: string;
}

export interface ActivityRowProps {
  title?: string | Record<string, string>;
  /** Which result bucket to render. Default {@code upcoming}. */
  bucket?: 'upcoming' | 'missed' | 'previous' | 'all';
  firstResult?: number;
  maxResults?: number;
  sortField?: string;
  sortOrder?: 'ASCENDING' | 'DESCENDING';
  filter?: ActivityFilter;
  /** Label for the detail/inspect link (card2). Localizable. */
  detailLabel?: string | Record<string, string>;
  /** Label for the apply button (card2). Localizable. */
  applyLabel?: string | Record<string, string>;
  /** Label shown after a successful application (card2). Localizable. */
  appliedLabel?: string | Record<string, string>;
  /** Prefix shown before the last entry date (card2). Localizable. */
  lastEntryLabel?: string | Record<string, string>;
  /** Prefix shown before the course code value (card2). Localizable. */
  courseCodeLabel?: string | Record<string, string>;
  /** Suffix shown after the duration number, e.g. "Saat" (card2). Localizable. */
  hourLabel?: string | Record<string, string>;
  /** Prefix shown before the start date (card2). Localizable. */
  startDateLabel?: string | Record<string, string>;
  /** Prefix shown before the end date (card2). Localizable. */
  endDateLabel?: string | Record<string, string>;
  /** Prefix shown before the duration number (card2). Localizable. */
  durationLabel?: string | Record<string, string>;
  /** Prefix shown before the entries/quota number (card2). Localizable. */
  entriesLabel?: string | Record<string, string>;
  /** Section action button label (e.g. "Etkinlikler"). Localizable. */
  ctaLabel?: string | Record<string, string>;
  /** Section action button href. When empty, the button is hidden. */
  ctaHref?: string;
  /**
   * Auth-gate the whole block. {@code 'any'} (default) always renders;
   * {@code 'in'} only for signed-in users; {@code 'out'} only for
   * anonymous visitors (e.g. a "Sign in to see events" variant).
   */
  requireAuth?: 'any' | 'in' | 'out';
  style?: CSSProperties;
  /** When true, renders the section even if the feed returns no items.
   *  Useful for authenticated-only sources where an empty result for a
   *  guest should still show the block to logged-in users. */
  showWhenDataIsNull?: boolean;
  paddingOuter?: PaddingBox;
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  paddingInner?: PaddingBox;
}

/** One activity card — the normalised shape returned by the portal
 *  {@code /api/activities} route. */
interface Activity {
  id: number | string;
  title?: string;
  description?: string;
  startDate?: number;
  endDate?: number;
  lastEntryDate?: number;
  url?: string;
  city?: string;
  /** Venue detail beyond the city (9465). */
  district?: string;
  address?: string;
  maxEntries?: number;
  duration?: number;
  courseCode?: string;
  status?: string;
  educationLevels?: string[];
  thumbnail?: string;
  entriesClosed?: boolean;
  quotaFull?: boolean;
  registered?: boolean;
  /**
   * 9476 SHOW_BLOCKED — false means the tenant screens eligibility and this
   * viewer fails it. Undefined is "not screened", never "ineligible".
   */
  eligible?: boolean;
  /** Backend-localised reason behind `eligible: false`. */
  ineligibleReason?: string;
  /** Dynamic application form id (9464) — forwarded to the apply modal. */
  applicationFormId?: number;
}

/** Resolved card-2 labels (localizable in the admin, Turkish defaults). */
interface Card2Labels {
  detail: string;
  apply: string;
  applied: string;
  /** 9476 SHOW_BLOCKED — the disabled apply control's text. */
  ineligible: string;
  lastEntry: string;
  courseCode: string;
  hour: string;
  startDate: string;
  endDate: string;
  duration: string;
  entries: string;
}

/** Backend education-level code -> catalogue key. Resolved lazily so a
 *  language switch re-renders the labels. */
const EDU_LABEL_KEYS: Record<string, string> = {
  PRIMARY_SCHOOL: 'activity.educationLevel.primarySchool',
  MIDDLE_SCHOOL: 'activity.educationLevel.middleSchool',
  HIGH_SCHOOL: 'activity.educationLevel.highSchool',
  ASSOCIATE_DEGREE: 'activity.educationLevel.associateDegree',
  BACHELORS_DEGREE: 'activity.educationLevel.bachelorsDegree',
  MS_MBA: 'activity.educationLevel.msMba',
  DOCTORAL: 'activity.educationLevel.doctoral',
};

interface ActivitiesResponse {
  upcoming?: Activity[];
  missed?: Activity[];
  previous?: Activity[];
}

const BASE = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');

/** Above this many cards the grid becomes a scrollable rail with header arrows. */
const RAIL_THRESHOLD = 3;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

async function fetchActivities(
  body: Record<string, unknown>,
  signal: AbortSignal,
): Promise<ActivitiesResponse | null> {
  let res: Response;
  try {
    res = await fetch(`${BASE}/api/activities`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      signal,
    });
  } catch {
    return null;
  }
  if (!res.ok) return null;
  return (await res.json()) as ActivitiesResponse;
}

function pickBucket(data: ActivitiesResponse, bucket: ActivityRowProps['bucket']): Activity[] {
  const up = data.upcoming ?? [];
  const missed = data.missed ?? [];
  const prev = data.previous ?? [];
  switch (bucket) {
    case 'missed':
      return missed;
    case 'previous':
      return prev;
    case 'all':
      return [...up, ...missed, ...prev];
    case 'upcoming':
    default:
      return up;
  }
}

export function ActivityRow({
  title,
  bucket = 'upcoming',
  firstResult,
  maxResults,
  sortField,
  sortOrder,
  filter,
  detailLabel,
  applyLabel,
  appliedLabel,
  lastEntryLabel,
  courseCodeLabel,
  hourLabel,
  startDateLabel,
  endDateLabel,
  durationLabel,
  entriesLabel,
  ctaLabel,
  ctaHref,
  requireAuth,
  showWhenDataIsNull = false,
  style,
  paddingOuter,
  headingLine,
  paddingInner,
}: ActivityRowProps) {
  const { locale } = useLocale();
  const { t } = useTranslation('common');
  const { t: tb } = useTranslation('site-builder-blocks');
  const currentUser = useCurrentUser();

  // Auth gate — mirror the shell's requireAuth semantics.
  const isSignedIn = currentUser != null;
  const gate = requireAuth ?? 'any';
  const hidden = (gate === 'in' && !isSignedIn) || (gate === 'out' && isSignedIn);

  const first = clamp(typeof firstResult === 'number' ? firstResult : 0, 0, 10_000);
  const max = clamp(typeof maxResults === 'number' ? maxResults : 3, 1, 50);
  const sort = (sortField ?? '').trim() || 'recent';
  const order = sortOrder === 'DESCENDING' ? 'DESCENDING' : 'ASCENDING';

  // Stable, serialisable body — also the fetch dep key.
  const body = {
    firstResult: first,
    maxResults: max,
    sortField: sort,
    sortOrder: order,
    filter: {
      eventTypeIds: filter?.eventTypeIds ?? [],
      cityIds: filter?.cityIds ?? [],
      categoryIds: filter?.categoryIds ?? [],
      educationLevels: filter?.educationLevels ?? [],
      statuses: filter?.statuses ?? null,
      searchTerm: filter?.searchTerm ?? '',
    },
  };
  const bodyKey = JSON.stringify(body);

  const card2Labels: Card2Labels = {
    detail: tx(detailLabel) || tb('activity.card.detail'),
    apply: tx(applyLabel) || tb('activity.card.apply'),
    applied: tx(appliedLabel) || tb('activity.card.applied'),
    ineligible: t('activity.ineligible') || tb('activity.card.ineligible'),
    lastEntry: tx(lastEntryLabel) || tb('activity.card.lastEntry'),
    courseCode: tx(courseCodeLabel) || tb('activity.card.courseCode'),
    hour: tx(hourLabel) || tb('activity.card.hour'),
    startDate: t('activity.startDate') || tx(startDateLabel) || tb('activity.card.startDate'),
    endDate: t('activity.endDate') || tx(endDateLabel) || tb('activity.card.endDate'),
    duration: t('activity.duration') || tx(durationLabel) || tb('activity.card.duration'),
    entries: t('activity.entries') || tx(entriesLabel) || tb('activity.card.entries'),
  };

  const state = useAsyncResource<ActivitiesResponse>(
    (signal) => (hidden ? Promise.resolve(null) : fetchActivities(body, signal)),
    [bodyKey, hidden],
  );

  const heading = tx(title);
  const ctaText = tx(ctaLabel);
  const ctaTarget = typeof ctaHref === 'string' && ctaHref.trim() ? ctaHref.trim() : undefined;
  const ctaNode: ReactNode = ctaTarget ? (
    <PortalLink href={ctaTarget} style={sectionCtaStyle}>
      {ctaText || tb('activity.row.heading')} →
    </PortalLink>
  ) : null;

  const items = state.kind === 'loaded' ? pickBucket(state.data, bucket).slice(0, max) : [];
  // Past three cards the grid would wrap onto a second row; switch to a
  // horizontal rail instead and page it with header arrows (the
  // LearningJourneyPlans pattern). Three or fewer keeps the plain grid.
  const isRail = items.length > RAIL_THRESHOLD;

  // ── Rail scroll state — drives the prev/next buttons in the header ──
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    if (!isRail) {
      setCanLeft(false);
      setCanRight(false);
      return;
    }
    updateScrollState();
    const el = scrollerRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, isRail, items.length]);

  // Page by whole cards so the rail always lands on a card edge (the CSS
  // sizes items to exactly fill the viewport, so a % of clientWidth would
  // stop mid-card).
  const scrollByPage = useCallback((dir: 'left' | 'right') => {
    const el = scrollerRef.current;
    if (!el) return;
    const item = el.firstElementChild as HTMLElement | null;
    const gap = parseFloat(getComputedStyle(el).columnGap || '0') || 0;
    const step = item ? item.offsetWidth + gap : el.clientWidth;
    const perView = Math.max(1, Math.round(el.clientWidth / step));
    const amount = step * perView;
    el.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
  }, []);

  // Only worth showing arrows when the rail actually has somewhere to go —
  // four wide cards may still fit on a large screen.
  const hasOverflow = canLeft || canRight;

  let content: ReactNode;
  if (state.kind === 'loading') {
    content = (
      <div className="akd-card2-grid" style={gridCard2Style}>
        {Array.from({ length: Math.min(max, 3) }).map((_, i) => (
          <Card
            key={i}
            style={{
              ...card2Style,
              opacity: 0.55,
              minHeight: 220,
            }}
          />
        ))}
      </div>
    );
  } else if (state.kind === 'error') {
    content = <p style={messageStyle}>{tb('activity.row.loadFailed')}</p>;
  } else if (items.length === 0) {
    if (!showWhenDataIsNull) content = null;
    else content = <p style={messageStyle}>{tb('activity.row.empty')}</p>;
  } else {
    const cards = items.map((item) => (
      <ActivityCardV2 key={item.id} item={item} locale={locale} labels={card2Labels} />
    ));
    content = isRail ? (
      <div
        ref={scrollerRef}
        className="akd-act-scroller"
        role="region"
        aria-label={heading || tb('activity.row.heading')}
      >
        {items.map((item, i) => (
          <div key={item.id} className="akd-act-item">
            {cards[i]}
          </div>
        ))}
      </div>
    ) : (
      <div className="akd-card2-grid" style={gridCard2Style}>
        {cards}
      </div>
    );
  }

  if (hidden || content === null) return null;

  // Prev/next sit to the LEFT of the "see all" link, sharing one action group.
  const showNav = isRail && hasOverflow;
  const actionsNode: ReactNode =
    showNav || ctaNode ? (
      <div className="akd-act-actions">
        {showNav && (
          <div className="akd-act-nav">
            <button
              type="button"
              className="akd-act-navbtn"
              aria-label={tb('activity.row.scrollBack')}
              disabled={!canLeft}
              onClick={() => scrollByPage('left')}
            >
              <ChevronLeftGlyph />
            </button>
            <button
              type="button"
              className="akd-act-navbtn"
              aria-label={tb('activity.row.scrollForward')}
              disabled={!canRight}
              onClick={() => scrollByPage('right')}
            >
              <ChevronRightGlyph />
            </button>
          </div>
        )}
        {ctaNode}
      </div>
    ) : null;

  return (
    <BlockRoot
      as="section"
      style={{ ...sectionPad, ...resolvePaddingStyle(paddingOuter), ...style }}
    >
      <style>{HOVER_CSS}</style>
      <div
        className="akd-act-container"
        style={{ ...sectionInner, ...resolvePaddingStyle(paddingInner) }}
      >
        {heading || actionsNode ? (
          <header style={headerStyle}>
            {heading ? <h2 style={titleStyle}>{heading}</h2> : <span />}
            {heading && headingLine && <HeadingLine />}
            {actionsNode}
          </header>
        ) : null}
        {content}
      </div>
    </BlockRoot>
  );
}

function ChevronLeftGlyph(): ReactNode {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightGlyph(): ReactNode {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Card — wide training card (thumbnail-left)
// ---------------------------------------------------------------------------

function ActivityCardV2({
  item,
  locale,
  labels,
}: {
  item: Activity;
  locale: string;
  labels: Card2Labels;
}): ReactNode {
  const { t } = useTranslation('site-builder-blocks');
  const currentUser = useCurrentUser();
  const isSignedIn = currentUser != null;
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);

  const startDateText = item.startDate ? formatFull(item.startDate, locale) : '';
  const endDateText = item.endDate ? formatFull(item.endDate, locale) : '';
  const deadline = item.lastEntryDate ? formatFull(item.lastEntryDate, locale) : '';
  const ctaHref = isUrl(item.url) ? item.url : undefined;
  // The detail link accepts any non-empty url value (not just http(s)),
  // since the backend may return a relative path / slug.
  const detailHref = typeof item.url === 'string' && item.url.trim() ? item.url.trim() : undefined;
  const durationH = item.duration ? Math.round(item.duration / 60) : null;
  const eduText = item.educationLevels
    ?.map((e) => {
      const key = EDU_LABEL_KEYS[e];
      return key ? t(key) : e;
    })
    .join(', ');
  const slotsOpen = !item.entriesClosed && !item.quotaFull;
  const alreadyRegistered = Boolean(item.registered) || applied;

  return (
    <Card asChild>
      <article className="akd-card2" style={card2Style}>
        {/* Top: thumbnail + primary content */}
        <div className="akd-card2-top" style={c2TopStyle}>
          <div className="akd-card2-thumb" style={c2ThumbWrapStyle}>
            {item.thumbnail ? (
              <img
                src={item.thumbnail}
                alt=""
                style={c2ThumbImgStyle}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <div style={c2ThumbPlaceholderStyle}>
                <IconCalendar />
              </div>
            )}
          </div>

          <div style={c2BodyStyle}>
            {shortVenue(item) ? (
              <div style={c2LocationStyle}>
                <IconMapPin size={20} />
                {shortVenue(item)}
              </div>
            ) : null}
            {ctaHref ? (
              <a
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="akd-card2-title"
                style={c2TitleLinkStyle}
              >
                {item.title || t('activity.card.untitled')}
              </a>
            ) : (
              <div style={c2TitleStyle}>{item.title || t('activity.card.untitled')}</div>
            )}

            <div style={c2MetaListStyle}>
              {startDateText ? (
                <span style={metaItemStyle}>
                  <IconCalendar />
                  {labels.startDate} {startDateText}
                </span>
              ) : null}
              {endDateText ? (
                <span style={metaItemStyle}>
                  <IconCalendar />
                  {labels.endDate} {endDateText}
                </span>
              ) : null}
              {durationH !== null ? (
                <span style={metaItemStyle}>
                  <IconClock />
                  {labels.duration} {durationH} {labels.hour}
                </span>
              ) : null}
              {item.maxEntries && item.maxEntries > 0 ? (
                <span style={metaItemStyle}>
                  <IconUsers />
                  {labels.entries} {item.maxEntries}
                </span>
              ) : null}
              {eduText ? (
                <span style={metaItemStyle}>
                  <IconUser />
                  {eduText}
                </span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Deadline */}
        {deadline ? (
          <div style={c2InfoBlockStyle}>
            <div style={c2DeadlineStyle}>
              <strong style={c2DeadlineLabelStyle}>{labels.lastEntry}</strong> {deadline}
            </div>
          </div>
        ) : null}

        <div style={c2DividerStyle} />

        {/* Footer: details link + apply button */}
        <div style={c2FooterStyle}>
          {detailHref ? (
            <a
              href={detailHref}
              target="_blank"
              rel="noopener noreferrer"
              className="akd-card2-detail"
              style={c2DetailLinkStyle}
            >
              <IconInfo />
              {labels.detail}
            </a>
          ) : (
            <span style={c2DetailLinkStyle}>
              <IconInfo />
              {labels.detail}
            </span>
          )}
          {!slotsOpen ? null : alreadyRegistered ? (
            <span className="akd-card2-apply" style={{ ...c2ApplyBtnStyle, ...c2AppliedBtnStyle }}>
              {labels.applied}
            </span>
          ) : item.eligible === false ? (
            // 9476 SHOW_BLOCKED — listed, but not applicable by this viewer.
            <span
              className="akd-card2-apply"
              style={{ ...c2ApplyBtnStyle, ...c2AppliedBtnStyle }}
              title={item.ineligibleReason}
            >
              {labels.ineligible}
            </span>
          ) : isSignedIn ? (
            <button
              type="button"
              className="akd-card2-apply"
              style={c2ApplyButtonStyle}
              onClick={() => setApplyOpen(true)}
            >
              {labels.apply}
            </button>
          ) : (
            <PortalLink href="/sign-in" className="akd-card2-apply" style={c2ApplyBtnStyle}>
              {labels.apply}
            </PortalLink>
          )}
        </div>

        {isSignedIn && slotsOpen && item.eligible !== false ? (
          <ActivityApplicationModal
            open={applyOpen}
            onOpenChange={setApplyOpen}
            eventId={item.id}
            applicationFormId={item.applicationFormId}
            eventTitle={item.title}
            eventSubtitle={fullVenue(item)}
            onSuccess={() => setApplied(true)}
          />
        ) : null}
      </article>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

function formatFull(ms: number, locale: string): string {
  try {
    return new Date(ms).toLocaleDateString(locale || 'tr', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

function isUrl(v?: string): v is string {
  return typeof v === 'string' && /^https?:\/\//i.test(v.trim());
}

// ---------------------------------------------------------------------------
// Inline SVG icons (no icon-font dependency)
// ---------------------------------------------------------------------------

const ICON_PROPS = {
  width: 14,
  height: 14,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  style: { flexShrink: 0 } as CSSProperties,
};

function IconCalendar(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
  );
}
function IconMapPin({ size }: { size?: number } = {}): ReactNode {
  return (
    <svg {...ICON_PROPS} width={size ?? ICON_PROPS.width} height={size ?? ICON_PROPS.height}>
      <path d="M12 21s-7-5.5-7-11a7 7 0 0 1 14 0c0 5.5-7 11-7 11Z" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}
function IconUsers(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function IconClock(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 3" />
    </svg>
  );
}
function IconUser(): ReactNode {
  return (
    <svg {...ICON_PROPS}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}
function IconInfo(): ReactNode {
  return (
    <svg {...ICON_PROPS} width={18} height={18}>
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4M12 8h.01" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Styles — theme tokens with inline fallbacks (blocks convention)
// ---------------------------------------------------------------------------

const HOVER_CSS = `
.akd-card2{transition:transform .15s ease}
.akd-card2:hover{transform:translateY(-2px)}
.akd-card2-title:hover{text-decoration:underline}
.akd-card2-detail:hover{color:var(--color-text-primary,#111827)}
.akd-card2-apply{transition:filter .15s ease}
.akd-card2-apply:hover{filter:brightness(.94)}

/* ── Container queries, not viewport media queries ──────────────────────
   This block is routinely rendered next to a persistent sidebar, so its
   available width is narrower than the viewport — an @media breakpoint
   keyed to the window width stays a 3-column grid even when the block's
   own box is too narrow for three cards, causing horizontal overflow. */
.akd-act-container{container-type:inline-size;container-name:akd-act-row}

@container akd-act-row (max-width:1024px){
  .akd-card2-grid{grid-template-columns:repeat(2,minmax(0,1fr))!important}
}
@container akd-act-row (max-width:640px){
  .akd-card2-grid{grid-template-columns:minmax(0,1fr)!important}
  .akd-card2-top{flex-direction:column!important}
  .akd-card2-thumb{width:100%!important;height:auto!important;aspect-ratio:16/9!important}
  .akd-card2:hover{transform:none!important}
}

/* ── Header actions: prev/next next to the "see all" link ───────────── */
.akd-act-actions{display:inline-flex;align-items:center;gap:14px;align-self:center;flex-shrink:0}
.akd-act-nav{display:inline-flex;align-items:center;gap:8px}
.akd-act-navbtn{
  display:inline-flex;align-items:center;justify-content:center;
  width:38px;height:38px;padding:0;border-radius:999px;
  border:1px solid var(--color-neutral-200,#e4e4e7);
  background:var(--color-surface-card,#fff);
  color:var(--color-text-primary,#18181b);
  cursor:pointer;
  transition:background 150ms ease,border-color 150ms ease,opacity 150ms ease;
}
.akd-act-navbtn:hover:not(:disabled){
  background:var(--color-neutral-50,#fafafa);
  border-color:var(--color-neutral-300,#d4d4d8);
}
.akd-act-navbtn:focus-visible{outline:2px solid var(--color-brand-500,#3b5bdb);outline-offset:2px}
.akd-act-navbtn:disabled{opacity:.4;cursor:default}
.akd-act-navbtn svg{display:block}

/* ── Rail (only used past RAIL_THRESHOLD cards) ─────────────────────────
   Cards are sized so EXACTLY three fill the viewport width — no clipped
   fourth card peeking in and breaking the layout. Paging is done with the
   header arrows. */
.akd-act-scroller{
  --akd-act-gap:20px;
  --akd-act-per-view:3;
  display:flex;gap:var(--akd-act-gap);
  overflow-x:auto;overflow-y:hidden;
  scroll-snap-type:x mandatory;scroll-behavior:smooth;
  scrollbar-width:none;-ms-overflow-style:none;
  padding-block:2px;
}
.akd-act-scroller::-webkit-scrollbar{display:none;width:0;height:0}
.akd-act-item{
  flex:0 0 auto;min-width:0;
  width:calc(
    (100% - var(--akd-act-gap) * (var(--akd-act-per-view) - 1)) / var(--akd-act-per-view)
  );
  display:flex;flex-direction:column;scroll-snap-align:start;
}
/* the card fills its rail slot so every card in the row matches height */
.akd-act-item > *{height:100%}
@container akd-act-row (max-width:1024px){
  .akd-act-scroller{--akd-act-per-view:2}
}
@container akd-act-row (max-width:640px){
  /* single card per view, with a sliver of the next one as a scroll hint */
  .akd-act-scroller{--akd-act-gap:14px}
  .akd-act-item{width:88%}
}
@media(prefers-reduced-motion:reduce){
  .akd-act-scroller{scroll-behavior:auto}
  .akd-act-navbtn{transition:none}
}
`;

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
  marginBottom: 16,
};

const titleStyle: CSSProperties = {
  fontSize: 'var(--font-size-2xl, 22px)',
  fontWeight: 600,
  color: 'var(--color-text-primary, #111827)',
  margin: 0,
};

const sectionCtaStyle: CSSProperties = {
  color: 'var(--color-brand-600, #d6007f)',
  fontWeight: 'var(--font-weight-medium, 500)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};

const metaItemStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
};

const messageStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #5f6675)',
  padding: 'var(--space-6, 24px) 0',
  textAlign: 'center',
};

// ── Card variant "card2" styles ────────────────────────────────────────────

const gridCard2Style: CSSProperties = {
  display: 'grid',
  gap: 20,
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  // Cards in a row share the tallest card's height so their footers can line up.
  alignItems: 'stretch',
};

const card2Style: CSSProperties = {
  ...cardReset,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
  height: '100%',
  background: 'var(--color-surface-card, #fff)',
  borderRadius: 'var(--radius-xl, 16px)',
  padding: 'var(--space-5, 20px)',
};

/** Grows to absorb the card's spare height, so a title that wraps onto an
 *  extra line doesn't push the deadline / divider / footer out of line with
 *  the neighbouring cards — they stay pinned to the bottom. */
const c2TopStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 16,
  flex: 1,
  minHeight: 0,
};

const c2ThumbWrapStyle: CSSProperties = {
  flexShrink: 0,
  width: 132,
  height: 132,
  borderRadius: 'var(--radius-lg, 12px)',
  overflow: 'hidden',
  background: 'var(--color-surface-muted, #f3f4f6)',
};

const c2ThumbImgStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const c2ThumbPlaceholderStyle: CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'var(--color-brand-400, #93a8f5)',
};

const c2BodyStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const c2LocationStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 'var(--font-size-lg, 18px)',
  fontWeight: 700,
  color: 'var(--color-brand-600, #1d6fd6)',
  textTransform: 'uppercase' as const,
  letterSpacing: 'var(--font-tracking-wider, 0.04em)',
};

const c2TitleLinkStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg, 18px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
  textDecoration: 'none',
  lineHeight: 1.3,
};

const c2TitleStyle: CSSProperties = {
  fontSize: 'var(--font-size-lg, 18px)',
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
  lineHeight: 1.3,
};

const c2MetaListStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  marginTop: 4,
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #6b7280)',
};

const c2InfoBlockStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const c2DeadlineStyle: CSSProperties = {
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-primary, #111827)',
};

const c2DeadlineLabelStyle: CSSProperties = {
  fontWeight: 700,
  color: 'var(--color-text-primary, #111827)',
};

const c2DividerStyle: CSSProperties = {
  height: 1,
  background: 'var(--color-border-subtle, #e5e7eb)',
  margin: '0 -4px',
};

const c2FooterStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
};

const c2DetailLinkStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: 'var(--font-size-sm, 14px)',
  color: 'var(--color-text-secondary, #6b7280)',
  textDecoration: 'underline',
  textUnderlineOffset: '3px',
  cursor: 'pointer',
};

const c2ApplyBtnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 120,
  height: 48,
  padding: '0 28px',
  borderRadius: 'var(--radius-full, 999px)',
  fontSize: 'var(--font-size-base, 16px)',
  fontWeight: 700,
  textDecoration: 'none',
  cursor: 'pointer',
  background: 'var(--color-success-fg, #3fa45a)',
  color: 'var(--color-text-on-brand, #fff)',
};

/** Button-element variant of {@link c2ApplyBtnStyle} (strips the UA border
 *  and inherits the font so a {@code <button>} matches the anchor look). */
const c2ApplyButtonStyle: CSSProperties = {
  ...c2ApplyBtnStyle,
  border: 'none',
  fontFamily: 'inherit',
};

/** Muted, non-interactive look once the user has applied. */
const c2AppliedBtnStyle: CSSProperties = {
  cursor: 'default',
  background: 'var(--color-surface-muted, #eef0f2)',
  color: 'var(--color-text-secondary, #6b7280)',
};
